import { useState } from "react";
import { Select } from "../input-functions";
import "./styles.css";

const DEFAULT_NUMER_OF_ENTRIES_PER_PAGE = 10;
const LIST_OF_COUNT_PER_PAGE = [10, 50, 100];

export function calculatePageCount(
  total: number | undefined,
  perPageCount?: number
) {
  if (!total || !perPageCount) return 0;
  return Math.ceil(total / perPageCount);
}

function getRelativeEntryNumber(
  page: number,
  perPageCount: number,
  total?: number
) {
  const start = perPageCount * (page - 1) + 1;
  const end = start + perPageCount - 1;
  if (total !== undefined) {
    const endCount = end > total ? total : end;
    return `${start} - ${endCount} of ${total}`;
  }
  return `Page ${page}`;
}

interface PaginationProps {
  currentPage: number;
  totalEntries?: number;
  hasNextPage?: boolean;
  numberOfEntriesPerPage?: number;
  onPageChange?: (pageNumber: number) => void;
  onPerPageCountChange?: (numberOfEntriesPerPage: number) => void;
  previousText?: string;
  nextText?: string;
}

export default function Pagination({
  currentPage,
  totalEntries,
  hasNextPage,
  numberOfEntriesPerPage = DEFAULT_NUMER_OF_ENTRIES_PER_PAGE,
  onPageChange,
  onPerPageCountChange,
  previousText = "<",
  nextText = ">",
}: PaginationProps) {
  const totalPageCount = calculatePageCount(
    totalEntries,
    numberOfEntriesPerPage
  );
  const [jumpTo, setJumpTo] = useState("");

  // When totalEntries is unknown, derive hasNextPage from prop
  const unknownTotal = totalEntries === undefined;
  const canGoForward = unknownTotal ? !!hasNextPage : currentPage < totalPageCount;

  function handleChange(pageNo: number | string) {
    if (typeof pageNo === "string") return;
    if (onPageChange) {
      onPageChange(pageNo);
    }
  }

  function handleForward() {
    handleChange(currentPage + 1);
  }
  function handleBackward() {
    handleChange(currentPage - 1);
  }

  function handleJumpToSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!jumpTo) return;
    const jumpToPageNumber = Number(jumpTo);
    handleChange(jumpToPageNumber);
    setJumpTo("");
  }

  if (!unknownTotal && totalPageCount < 1) return null;
  if (unknownTotal && currentPage === 1 && !hasNextPage) return null;

  return (
    <nav
      aria-labelledby="Table Pagination"
      className="rf-pagination d-flex flex-lg-row flex-column align-items-center justify-content-lg-between"
    >
      <div className="d-flex flex-sm-row flex-column align-items-center mb-2 mb-lg-0">
        <ul className="rf-pagination__list mb-2 mb-sm-0">
          <li className="rf-pagination__item">
            <button
              className="rf-pagination__btn--action"
              onClick={handleBackward}
              disabled={currentPage === 1}
            >
              {previousText}
            </button>
          </li>
          <div className="rf-pagination__relative-entry-count">
            {getRelativeEntryNumber(
              currentPage,
              numberOfEntriesPerPage,
              totalEntries,
            )}
          </div>
          <li className="rf-pagination__item">
            <button
              className="rf-pagination__btn--action"
              onClick={handleForward}
              disabled={!canGoForward}
            >
              {nextText}
            </button>
          </li>
        </ul>
        {!unknownTotal && (
        <form className="rf-pagination__jump-to" onSubmit={handleJumpToSubmit}>
          <span className="rf-pagination__jump-to--text">Jump to page</span>
          <input
            name="jumpTo"
            type="number"
            className="rf-pagination__jump-to--input"
            min={1}
            max={totalPageCount}
            value={jumpTo}
            onChange={(e) => setJumpTo(e.target.value)}
            required
          />
        </form>
        )}
      </div>
      <div
        className="d-flex align-items-center"
        style={{ whiteSpace: "nowrap" }}
      >
        <span>Show</span>
        <Select
          className="rf-pagination__per-page-selector mx-2"
          options={LIST_OF_COUNT_PER_PAGE.map((count) => ({
            label: count,
            value: count,
          }))}
          value={numberOfEntriesPerPage}
          onChange={(e) => {
            if (onPerPageCountChange) {
              onPerPageCountChange(Number(e.target.value));
            }
          }}
        />
        <span>per page</span>
      </div>
    </nav>
  );
}
